import { map, switchMap, tap } from 'rxjs/operators';
import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TokenService } from 'services/token/token.service';
import { TokenStorageService } from 'services/token/token-storage.service';
import { EntryService } from 'services/entry/entry.service';
import { Store } from '@ngrx/store';
import { Actions } from 'features/profiles-state/+state/profiles-state.actions';
import { requestCompleted } from '@certemy/common';
import { Selectors } from 'features/profiles-state/+state/profiles-state.state';
import { LOGIN_ERRORS, LOGIN_PROVIDER, SIGN_IN_TYPES, VerifyTokenData } from 'pages/entry/entry.helpers';
import _get from 'lodash-es/get';
import { Actions as BrandingActions } from 'features/organization-settings/platform-branding/+state/platform-branding.actions';
import { Observable } from 'rxjs';
import { ExternalLoginSettings } from 'interfaces/external-login.interface';
import { Selectors as LoginProviderSelectors } from 'services/login-provider/+state/login-provider.state';
import { LoginProviderRedirectResponse } from 'services/login-provider/login-provider.interface';
import _set from 'lodash-es/set';

@Component({
  selector: 'cm-existing-account',
  styleUrls: ['./existing-account.component.scss'],
  templateUrl: './existing-account.component.html',
})
export class ExistingAccountComponent implements OnInit {
  form: UntypedFormGroup;
  submitted = false;
  model: any = {
    email: '',
  };
  submitBtnDisabled = false;
  epResult = true;
  errorMessage = '';
  signInType: SIGN_IN_TYPES = SIGN_IN_TYPES.DEFAULT;
  loginProvider: LOGIN_PROVIDER = LOGIN_PROVIDER.CERTEMY;
  showPasswords = true;
  showExternalLogin = true;
  private loginToken = '';
  readonly SIGN_IN_TYPES = SIGN_IN_TYPES;
  readonly signInTypesArray = [
    SIGN_IN_TYPES.ONE_LOGIN,
    SIGN_IN_TYPES.OKTA,
    SIGN_IN_TYPES.AZURE,
    SIGN_IN_TYPES.ENTRA,
    SIGN_IN_TYPES.PING,
  ];

  constructor(
    private tokenService: TokenService,
    private tokenStorage: TokenStorageService,
    private router: Router,
    private entryService: EntryService,
    private store: Store<any>,
    private activeRoute: ActivatedRoute,
  ) {
    this.form = new UntypedFormGroup({
      password: new UntypedFormControl('', Validators.compose([Validators.required])),
    });
  }

  settings$: Observable<ExternalLoginSettings> = this.store
    .select(LoginProviderSelectors.loginProviderMappedState)
    .pipe(
      map(data => ({
        ...data,
        data: _get(this.model, 'payload.link'),
        url: window.location.href,
      })),
    );

  ngOnInit() {
    const queryParams = this.activeRoute.snapshot.queryParams;
    const { accessToken, type, organizationId, data, error } = queryParams as LoginProviderRedirectResponse;
    if (this.entryService.getMessageType().email) {
      this.model = this.entryService.getMessageType();
    } else if (this.signInTypesArray.includes(type)) {
      if (error === LOGIN_ERRORS.ACCESS_DENIED) {
        this.epResult = false;
        this.errorMessage = LOGIN_ERRORS.INCORRECT_ACCOUNT;
      } else if (accessToken) {
        this.loginToken = accessToken;
        _set(this.model, 'payload.link', data);
        _set(this.model, 'payload.organizationId', organizationId);
        this.model.isSelfEnrollment = true;
        this.verifyToken(accessToken, type, Number(organizationId));
      }
    } else {
      this.router.navigate(['/entry/login']);
    }
  }

  async handleCredentialResponse(response) {
    const { credential, type } = response;
    this.loginToken = credential;
    await this.verifyToken(credential, type);
  }

  private verifyToken(token: string, type: SIGN_IN_TYPES, organizationId?: number) {
    this.tokenService.verifyUserByToken(token, type, organizationId).subscribe((result: VerifyTokenData) => {
      this.model.email = result.email;
      this.form.patchValue({
        password: 'nullN_11',
      });
      // this.form.get('password').disable();
      this.signInType = type;
      this.loginProvider =
        this.signInType === SIGN_IN_TYPES.ONE_LOGIN
          ? LOGIN_PROVIDER.ONE_LOGIN
          : this.signInType === SIGN_IN_TYPES.OKTA
          ? LOGIN_PROVIDER.OKTA
          : this.signInType === SIGN_IN_TYPES.PING
          ? LOGIN_PROVIDER.PING
          : this.signInType === SIGN_IN_TYPES.ENTRA
            ? LOGIN_PROVIDER.ENTRA
          : this.signInType === SIGN_IN_TYPES.AZURE
          ? LOGIN_PROVIDER.AZURE
          : result.domain
          ? LOGIN_PROVIDER.GOOGLE_DOMAIN
          : LOGIN_PROVIDER.GOOGLE;
      this.showPasswords = false;
      this.showExternalLogin = false;
    });
  }

  onSubmit(values: any): void {
    this.submitted = true;
    this.epResult = true;

    const handleToken = codeName => {
      this.epResult = codeName !== 'FAILED';
      if (this.model.isSelfEnrollment) {
        this.entryService.setMessageType({
          isSelfEnrollment: this.model.isSelfEnrollment,
          uuid: this.model.uuid,
        });
      }
    };

    if (this.form.valid) {
      this.submitBtnDisabled = true;
      const isSelfEnrollment = this.model.isSelfEnrollment;
      const defaultPayload = {
        username: this.model.email,
        password: values.password,
        ...([SIGN_IN_TYPES.GOOGLE, ...this.signInTypesArray].includes(
          this.signInType,
        )
          ? {
              signInType: this.signInType,
              login_provider: this.loginProvider,
              token: this.loginToken,
              organizationId: _get(this.model, 'payload.organizationId'),
            }
          : {}),
      };
      const link = _get(this.model, 'payload.link');

      const selfEnrollmentPayload = {
        ...defaultPayload,
        payload: { link },
      };

      const getUserLogin = isSelfEnrollment
        ? this.tokenService.getUserSelfEnrollmentLogin(selfEnrollmentPayload)
        : [SIGN_IN_TYPES.GOOGLE, ...this.signInTypesArray].includes(
            this.signInType,
          )
        ? this.tokenService.getUserLoginByToken(this.loginToken, this.signInType)
        : this.tokenService.getUserLogin(defaultPayload);

      getUserLogin
        .pipe(
          tap(handleToken),
          switchMap(() => {
            this.store.dispatch(new Actions.GetProfiles());
            this.store.dispatch(new BrandingActions.GetBrandingSettings());
            return requestCompleted(this.store.select(Selectors.profilesState));
          }),
        )
        .subscribe(isSuccess => this.redirectAfterLogin(isSuccess), error => this.handleError(error));
    } else {
      // As form contains of only one field we are displaying only one error message in all cases!
      this.epResult = false;
    }
  }

  back() {
    const { isSelfEnrollment, uuid, title } = this.model;
    if (isSelfEnrollment) {
      if (uuid && title) {
        return this.router.navigate([`/entry/self-enrollments/${title}/${uuid}`]);
      }
      return window.history.back();
    }
    return this.router.navigate(['/entry/login']);
  }

  private handleError(error) {
    this.epResult = false;
    if (!!_get(error.body, 'details.certificates.length')) {
      this.errorMessage = LOGIN_ERRORS.DRAFT_CERTIFICATION;
    } else {
      this.errorMessage = LOGIN_ERRORS.INCORRECT_PASSWORD;
    }
    this.submitBtnDisabled = false;
  }

  private redirectAfterLogin(isSuccess: boolean) {
    this.submitBtnDisabled = false;
    this.epResult = isSuccess;

    if (isSuccess && this.tokenStorage.isLoggedIn) {
      if (!this.model.newProfile) {
        this.store.dispatch(new Actions.Login());
      } else if (this.model.newProfile && this.model.msg === 'professional') {
        this.router.navigate(['/entry/first-login/professional']);
      } else if (this.model.newProfile && this.model.msg === 'organization') {
        this.router.navigate(['/entry/first-login/organization']);
      }
    }
  }
}
